import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  DEFAULT_VOLUME_SETTINGS,
  VolumeChannel,
  VolumeSettings,
  clampVolume,
  normalizeVolumeSettings,
} from './volumeSettings';

/**
 * The volume settings in Redux. Not redux-persisted: redux-persist rehydrates
 * once at startup, but these must follow the signed-in user when one account
 * signs out and another signs in without a reload. `useVolumeSettingsSync`
 * loads them from the user's namespaced storage and saves them back.
 */
const volumeSlice = createSlice({
  name: 'volume',
  initialState: DEFAULT_VOLUME_SETTINGS,
  reducers: {
    setChannelVolume(
      state,
      action: PayloadAction<{ channel: VolumeChannel; volume: number }>,
    ): VolumeSettings {
      const volume = clampVolume(action.payload.volume);
      if (volume === null) return state;
      const { channel } = action.payload;
      return { ...state, [channel]: { ...state[channel], level: volume } };
    },
    setChannelMuted(
      state,
      action: PayloadAction<{ channel: VolumeChannel; muted: boolean }>,
    ): VolumeSettings {
      const { channel, muted } = action.payload;
      return { ...state, [channel]: { ...state[channel], muted } };
    },
    volumeSettingsHydrated(_state, action: PayloadAction<VolumeSettings>): VolumeSettings {
      return normalizeVolumeSettings(action.payload);
    },
  },
});

export const { setChannelVolume, setChannelMuted, volumeSettingsHydrated } = volumeSlice.actions;

export const volumeReducer = volumeSlice.reducer;

// Falls back to the defaults for a store built without this slice (test stores).
export const selectVolumeSettings = (state: { volumeReducer?: VolumeSettings }): VolumeSettings =>
  state.volumeReducer ?? DEFAULT_VOLUME_SETTINGS;
